import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, Tag, Card, Row, Col, Statistic, Button, Space,
  Modal, Form, Input, Checkbox, Alert, Badge, Typography, Tooltip, message,
} from 'antd';
import {
  WalletOutlined, SyncOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import API from '../api';
import dayjs from 'dayjs';

const { Text } = Typography;

interface Payout {
  id: number;
  providerId: number;
  providerName: string;
  providerEmail: string;
  providerPhone: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  amount: number;
  status: 'PENDING' | 'COMPLETED' | 'REJECTED' | 'PROCESSING' | 'APPROVED' | 'FAILED';
  txnId: string;
  requestedAt: string;
  processedAt?: string;
  processedBy?: string;
  reference?: string;
  adminNotes?: string;
}

const statusColor = (s: string) => ({
  PENDING:    'orange',
  APPROVED:   'cyan',
  PROCESSING: 'blue',
  COMPLETED:  'green',
  REJECTED:   'red',
  FAILED:     'volcano',
}[s] ?? 'default');

function maskAccount(n: string): string {
  if (!n || n.length < 4) return '••••';
  return '••••' + n.slice(-4);
}

function daysWaiting(iso: string): number {
  return dayjs().diff(dayjs(iso), 'day');
}

export default function Payouts() {
  const [payouts, setPayouts]       = useState<Payout[]>([]);
  const [loading, setLoading]       = useState(false);
  const [selected, setSelected]     = useState<Payout | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen]   = useState(false);
  const [viewOpen, setViewOpen]       = useState(false);
  const [approveForm] = Form.useForm();
  const [rejectForm]  = Form.useForm();
  const [confirmed, setConfirmed]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchPayouts = useCallback(() => {
    setLoading(true);
    API.get('/payouts')
      .then(r => setPayouts(r.data || []))
      .catch(() => message.error('Failed to load payouts'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPayouts();
    refreshTimer.current = setInterval(fetchPayouts, 60_000);
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
  }, [fetchPayouts]);

  // Summary stats
  const now     = dayjs();
  const pending = payouts.filter(p => p.status === 'PENDING');
  const thisMonth = payouts.filter(p => dayjs(p.requestedAt).isSame(now, 'month'));
  const paidThisMonth  = thisMonth.filter(p => p.status === 'COMPLETED').reduce((s, p) => s + Number(p.amount), 0);
  const totalThisMonth = thisMonth.length;

  const openApprove = (p: Payout) => {
    setSelected(p); setConfirmed(false);
    approveForm.resetFields();
    setApproveOpen(true);
  };

  const openReject = (p: Payout) => {
    setSelected(p);
    rejectForm.resetFields();
    setRejectOpen(true);
  };

  const handleApprove = async () => {
    if (!confirmed) { message.warning('Please confirm you have sent the EFT payment'); return; }
    try {
      const values = await approveForm.validateFields();
      setSubmitting(true);
      await API.post(`/payouts/${selected!.id}/approve`, {
        reference:   values.reference,
        notes:       values.notes || '',
        processedBy: 'admin',
      });
      message.success('Payout approved — provider notified');
      setApproveOpen(false);
      fetchPayouts();
    } catch (e: any) {
      if (e?.response?.data?.error) message.error(e.response.data.error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    try {
      const values = await rejectForm.validateFields();
      setSubmitting(true);
      await API.post(`/payouts/${selected!.id}/reject`, {
        reason:      values.reason,
        processedBy: 'admin',
      });
      message.success('Payout rejected — balance restored');
      setRejectOpen(false);
      fetchPayouts();
    } catch (e: any) {
      if (e?.response?.data?.error) message.error(e.response.data.error);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Provider',
      render: (_: any, r: Payout) => (
        <div>
          <Text strong>{r.providerName}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{r.providerPhone}</Text>
        </div>
      ),
    },
    {
      title: 'Bank',
      dataIndex: 'bankName',
    },
    {
      title: 'Account',
      render: (_: any, r: Payout) => (
        <Text code>{maskAccount(r.accountNumber)}</Text>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (v: number) => (
        <Text strong style={{ color: '#16a34a', fontSize: 15 }}>
          R{Number(v).toFixed(2)}
        </Text>
      ),
      sorter: (a: Payout, b: Payout) => Number(a.amount) - Number(b.amount),
    },
    {
      title: 'Requested',
      dataIndex: 'requestedAt',
      render: (v: string) => (
        <div>
          <div>{dayjs(v).format('DD MMM YYYY')}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {daysWaiting(v) === 0 ? 'Today' : `${daysWaiting(v)}d ago`}
          </Text>
        </div>
      ),
      sorter: (a: Payout, b: Payout) => dayjs(a.requestedAt).unix() - dayjs(b.requestedAt).unix(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v: string) => <Tag color={statusColor(v)}>{v}</Tag>,
      filters: [
        { text: 'Pending',   value: 'PENDING' },
        { text: 'Completed', value: 'COMPLETED' },
        { text: 'Rejected',  value: 'REJECTED' },
      ],
      onFilter: (value: any, r: Payout) => r.status === value,
    },
    {
      title: 'Actions',
      render: (_: any, r: Payout) => {
        if (r.status === 'PENDING') {
          return (
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                style={{ background: '#16a34a', borderColor: '#16a34a' }}
                onClick={() => openApprove(r)}
              >
                Approve
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => openReject(r)}
              >
                Reject
              </Button>
            </Space>
          );
        }
        return (
          <Button size="small" onClick={() => { setSelected(r); setViewOpen(true); }}>
            View Details
          </Button>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Summary cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title={
                <Space>
                  Pending Requests
                  {pending.length > 0 && (
                    <Badge count={pending.length} style={{ backgroundColor: '#f5222d' }} />
                  )}
                </Space>
              }
              value={pending.length}
              valueStyle={{ color: pending.length > 0 ? '#f5222d' : '#52c41a' }}
              prefix={<WalletOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Paid Out This Month"
              value={paidThisMonth}
              prefix="R"
              precision={2}
              valueStyle={{ color: '#16a34a' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Requests This Month"
              value={totalThisMonth}
            />
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Card
        title="Payout Requests"
        extra={
          <Button icon={<SyncOutlined />} onClick={fetchPayouts} loading={loading}>
            Refresh
          </Button>
        }
      >
        <Alert
          message="Manual EFT Flow"
          description="Process each withdrawal via your bank (GoTyme/FNB/etc.), then click Approve and enter the EFT reference. The provider is notified automatically."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Table
          dataSource={payouts}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="middle"
          rowClassName={(r: Payout) => r.status === 'PENDING' ? '' : ''}
          pagination={{ pageSize: 20, showSizeChanger: false }}
        />
      </Card>

      {/* Approve Modal */}
      <Modal
        title={
          <Space>
            <CheckCircleOutlined style={{ color: '#16a34a' }} />
            Approve Payout
          </Space>
        }
        open={approveOpen}
        onOk={handleApprove}
        onCancel={() => setApproveOpen(false)}
        okText="Approve"
        okButtonProps={{ style: { background: '#16a34a', borderColor: '#16a34a' }, loading: submitting }}
        width={480}
      >
        {selected && (
          <>
            <Alert
              type="warning"
              showIcon
              icon={<ExclamationCircleOutlined />}
              message="Confirm you have manually processed this EFT before approving."
              style={{ marginBottom: 16 }}
            />
            <Card size="small" style={{ marginBottom: 16, background: '#f8fafc' }}>
              <Row gutter={8}>
                <Col span={12}><Text type="secondary">Provider</Text><br /><Text strong>{selected.providerName}</Text></Col>
                <Col span={12}><Text type="secondary">Amount</Text><br /><Text strong style={{ color: '#16a34a' }}>R{Number(selected.amount).toFixed(2)}</Text></Col>
                <Col span={12} style={{ marginTop: 8 }}><Text type="secondary">Bank</Text><br /><Text>{selected.bankName}</Text></Col>
                <Col span={12} style={{ marginTop: 8 }}><Text type="secondary">Account</Text><br /><Text>{maskAccount(selected.accountNumber)}</Text></Col>
                <Col span={24} style={{ marginTop: 8 }}><Text type="secondary">Account Holder</Text><br /><Text>{selected.accountHolder}</Text></Col>
              </Row>
            </Card>
            <Form form={approveForm} layout="vertical">
              <Form.Item name="reference" label="EFT Reference Number" rules={[{ required: true, message: 'Reference is required' }]}>
                <Input placeholder="e.g. GT20260417123456" />
              </Form.Item>
              <Form.Item name="notes" label="Admin Notes (optional)">
                <Input.TextArea rows={2} placeholder="Any notes for your records" />
              </Form.Item>
              <Form.Item>
                <Checkbox checked={confirmed} onChange={e => setConfirmed(e.target.checked)}>
                  I have sent the EFT payment of <Text strong>R{Number(selected.amount).toFixed(2)}</Text> to {selected.providerName}
                </Checkbox>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        title={
          <Space>
            <CloseCircleOutlined style={{ color: '#f5222d' }} />
            Reject Payout
          </Space>
        }
        open={rejectOpen}
        onOk={handleReject}
        onCancel={() => setRejectOpen(false)}
        okText="Reject Payout"
        okButtonProps={{ danger: true, loading: submitting }}
        width={420}
      >
        {selected && (
          <>
            <Card size="small" style={{ marginBottom: 16, background: '#fff7f7' }}>
              <Row gutter={8}>
                <Col span={12}><Text type="secondary">Provider</Text><br /><Text strong>{selected.providerName}</Text></Col>
                <Col span={12}><Text type="secondary">Amount</Text><br /><Text strong>R{Number(selected.amount).toFixed(2)}</Text></Col>
              </Row>
            </Card>
            <Alert
              type="info"
              showIcon
              message="The provider's balance will be restored automatically."
              style={{ marginBottom: 16 }}
            />
            <Form form={rejectForm} layout="vertical">
              <Form.Item name="reason" label="Reason for Rejection" rules={[{ required: true, message: 'Please provide a reason' }]}>
                <Input.TextArea rows={3} placeholder="e.g. Incorrect bank account details on file — please update and resubmit" />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* View Details Modal */}
      <Modal
        title="Payout Details"
        open={viewOpen}
        onCancel={() => setViewOpen(false)}
        footer={<Button onClick={() => setViewOpen(false)}>Close</Button>}
        width={480}
      >
        {selected && (
          <div>
            <Row gutter={[16, 12]}>
              <Col span={12}><Text type="secondary">Provider</Text><br /><Text strong>{selected.providerName}</Text></Col>
              <Col span={12}><Text type="secondary">Amount</Text><br /><Text strong style={{ color: '#16a34a' }}>R{Number(selected.amount).toFixed(2)}</Text></Col>
              <Col span={12}><Text type="secondary">Status</Text><br /><Tag color={statusColor(selected.status)}>{selected.status}</Tag></Col>
              <Col span={12}><Text type="secondary">Requested</Text><br /><Text>{dayjs(selected.requestedAt).format('DD MMM YYYY HH:mm')}</Text></Col>
              <Col span={12}><Text type="secondary">Bank</Text><br /><Text>{selected.bankName}</Text></Col>
              <Col span={12}><Text type="secondary">Account</Text><br /><Text code>{maskAccount(selected.accountNumber)}</Text></Col>
              {selected.processedAt && (
                <>
                  <Col span={12}><Text type="secondary">Processed</Text><br /><Text>{dayjs(selected.processedAt).format('DD MMM YYYY HH:mm')}</Text></Col>
                  <Col span={12}><Text type="secondary">Processed By</Text><br /><Text>{selected.processedBy}</Text></Col>
                </>
              )}
              {selected.reference && (
                <Col span={24}><Text type="secondary">EFT Reference</Text><br /><Text code>{selected.reference}</Text></Col>
              )}
              {selected.adminNotes && (
                <Col span={24}><Text type="secondary">{selected.status === 'REJECTED' ? 'Rejection Reason' : 'Notes'}</Text><br /><Text>{selected.adminNotes}</Text></Col>
              )}
              <Col span={24}><Text type="secondary">Txn ID</Text><br /><Text code style={{ fontSize: 11 }}>{selected.txnId}</Text></Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
}
